import axios from 'axios'
import ParamsUtil from 'utils/params-util'

const API_PATH = 'api/v2/admins/sales/deals'
const BUYING_API_PATH = 'api/v2/admins/buying/deals'

// NEW:
// Keep create payload compatible with React Select object payload
// and old plain-string payload from legacy forms.
const getPaymentMethodValue = (paymentMethod) => {
  if (!paymentMethod) return null
  if (typeof paymentMethod === 'string') return paymentMethod
  return paymentMethod?.value || paymentMethod?.method_type || paymentMethod?.payment_method || null
}

const getPaymentMethodCardTypeValue = (paymentMethod) => {
  if (!paymentMethod || typeof paymentMethod === 'string') return null
  return paymentMethod?.card_type || null
}

const getPaymentInstallmentValue = (paymentMethod) => {
  if (!paymentMethod || typeof paymentMethod === 'string') return null
  return paymentMethod?.installment ?? null
}

const SalesDealsApi = {
  /**
   * Create ::Sales::Deals
   * Invoke `POST admins/sales/deals
   * @param {Boolean} use_existing_user - whether to use existing (already created user) or add a new user from scratch
   * @param {Boolean} edit_existing_user - whether to edit existing (already created user) data
   * @param {Object} user_id - existing user id for which order will be created
   * @param {String} email - user-related field for new user (use_existing_user is false)
   * @param {String} password - user-related field for new user (use_existing_user is false)
   * @param {Date} birthdate - user-related field for new user (use_existing_user is false)
   * @param {String} name - user_detail-related field for new user (use_existing_user is false)
   * @param {String} phone - user_detail-related field for new user (use_existing_user is false)
   * @param {String} phone_code - user_detail-related field for new user (use_existing_user is false)
   * @param {String} instagram_account - user_detail-related field for new user (use_existing_user is false)
   * @param {String} lead_source - user_detail-related field for new user (use_existing_user is false)
   * @param {String} complete_address - address-related field for new user (use_existing_user is false)
   * @param {String} recipient_name - address-related field for new user (use_existing_user is false)
   * @param {String} recipient_number - address-related field for new user (use_existing_user is false)
   * @param {String} instruction - address-related field for new user (use_existing_user is false)
   * @param {Number} latitude - address-related field for new user (use_existing_user is false)
   * @param {Number} longitude - address-related field for new user (use_existing_user is false)
   * @param {Array<Object>} items - array of bought products
   * @param {Number} courier - selected shipment method with courier.rate.id field
   * @param {Object} payment_method - selected payment method {value: string, installment: nullable_int}
   * @param {String} voucher_code - selected voucher code
   * @param {String} sales_method - pre-defined option in frontend, free text in backend (warehouse sale, live auction, 24-hours auction, etc.)
   * @param {String} sales_name_in_invoice - name of sales to be displayed in the invoice
   * @param {String} deal_by - name of sales doing the dealing
   * @param {String} assist_by - name of sales doing the assisting
   * @param {String} note - free text
   * @param {Date} pickup_at - Estimated pick up date of the package
   * @param {Boolean} include_invoice - whether invoice should be included in the packaging
   * @param {Boolean} include_polaroid - whether polaroid should be included in the packaging
   * @param {Boolean} include_love_letter - whether love letter should be included in the packaging
   * @param {Number} down_payment - if not fully paid
   */
  create: async ({
    use_existing_user,
    edit_existing_user,
    user_id,
    user_detail_id,
    address_id,
    email,
    password,
    birthdate,
    name,
    phone,
    phone_code,
    instagram_account,
    lead_source,
    complete_address,
    recipient_name,
    recipient_number,
    instruction,
    latitude,
    longitude,
    items,
    courier,
    payment_method,
    card_type: raw_card_type,
    voucher_code,
    sales_method,
    sales_method_note,
    sales_name_in_invoice,
    deal_by,
    assist_by,
    assist_by_list,
    note,
    pickup_at,
    shipment_cost,
    include_invoice,
    include_polaroid,
    include_love_letter,
    down_payment,
    po_condition_id,
    po_comes_with,
    po_designer_id,
    po_category_id,
  }) => {
    const path = API_PATH

    const card_type = raw_card_type || getPaymentMethodCardTypeValue(payment_method)

    const params = {
      use_existing_user,
      edit_existing_user,
      user_id,
      user_detail_id,
      address_id,
      email,
      password,
      birthdate,
      name,
      phone,
      phone_code,
      instagram_account,
      lead_source,
      complete_address,
      recipient_name,
      recipient_number,
      instruction,
      latitude,
      longitude,

      items,
      rate_id: courier.rate.id,
      payment_method: getPaymentMethodValue(payment_method),
      installment: getPaymentInstallmentValue(payment_method),
      card_type,
      // NOTE:
      // Ongkir tetap ditampilkan di form create untuk kebutuhan operasional,
      // tetapi saat create deal Ready/BKO nilainya harus selalu start dari 0
      // agar tidak ikut masuk ke transaksi / invoice.
      shipment_cost: 0,
      voucher_code,
      sales_method,
      sales_method_note,
      sales_name_in_invoice,
      deal_by,
      assist_by,
      assist_by_list,
      note,
      pickup_at,
      include_invoice,
      include_polaroid,
      include_love_letter,
      down_payment,
      po_condition_id,
      po_comes_with,
      po_designer_id,
      po_category_id,
    }

    const response = await axios.post(path, params)

    return response
  },

  /**
   * Search product list formatted specfically for the purpose of making deals
   * Invoke `POST admins/sales/deals/search_live_products
   * @param {String} query - search products by name or SKU
   */
  searchLiveProducts: async ({ query }) => {
    const path = `${API_PATH}/search_live_products?q=${query}`

    const response = await axios.get(path)

    return response
  },

  /**
   * Check whether user email is unique
   * Invoke `POST admins/sales/deals/is_user_email_unique
   * @param {String} email - full email address
   */
  isUserEmailUnique: async ({ email }) => {
    const path = `${API_PATH}/is_user_email_unique?email=${encodeURIComponent(email)}`

    const response = await axios.get(path)

    return response
  },

  /**
   * Check whether user phone is unique
   * Invoke `POST admins/sales/deals/is_user_phone_unique
   * @param {String} phone - full phone address
   */
  isUserPhoneUnique: async ({ phone }) => {
    const path = `${API_PATH}/is_user_phone_unique?full_phone=${encodeURIComponent(phone)}`

    const response = await axios.get(path)

    return response
  },

  /**
   * Get couriers list for the purpose of making deals
   * Invoke `POST admins/sales/deals/couriers
   * @param {Number} latitude - latitude of user as buyer
   * @param {Number} longitude - longitude of user as buyer
   * @param {Array<Object>} product_id_with_selling_price_list - [{product_id: int, selling_price: int}]
   */
  couriers: async ({ latitude, longitude, product_id_with_selling_price_list }) => {
    const path = `${API_PATH}/couriers`

    const params = {
      latitude,
      longitude,
      product_id_with_selling_price_list,
    }

    const response = await axios.post(path, params)

    return response
  },

  /**
   * Get all OR filtered Deals
   * Invoke `GET admins/outbound/deals
   * @param {String} q - query to search by deal or payment reference numbers
   */
  get: async (rawParams = {}) => {
    const defaults = {
      page     : 1,
      per_page : 10,
      sort_by  : 'deals.created_at',
      sort_dir : 'desc',
      scope    : 'need_action', // align with frontend default
    }

    // Merge and drop empty keys so only real filters go to the server
    const params = ParamsUtil.removeEmptyAttributes({ ...defaults, ...rawParams })

    return axios.get(API_PATH, { params })
  },

  getCreatedByOptions: async () => {
    const path = `${API_PATH}/created_by_options`

    const response = await axios.get(path)

    return response
  },

  /**
   * Get specific deals
   * Invoke `GET admins/sales/deals/:id
   * @param {Number} dealId - id of Deal
   */
  show: async ({ dealId }) => {
    const path = `${API_PATH}/${dealId}`

    const response = await axios.get(path)

    return response
  },

  /**
   * Update specific deals
   * Invoke `PUT admins/sales/deals/:id
   * @param {Number} dealId - id of Deal
   * @param {Object} payload - updated deal data
   */
  update: async ({ dealId, payload }) => {
    const path = `${API_PATH}/${dealId}`

    const response = await axios.put(path, payload)

    return response
  },

  /**
   * Update Final Price deals
   * Invoke `PUT admins/sales/deals/:id/update_final_prices
   * @param {Number} dealId - id of Deal
   * @param {Object} payload - updated deal data
   */
  updateFinalPrices: async ({ dealId, payload }) => {
    const path = `${API_PATH}/${dealId}/update_final_prices`

    const response = await axios.put(path, payload)
    
    return response
  },

  /**
   * Get history list of a deal
   * Invoke `GET admins/sales/deals/:id/list_history
   * @param {Number} dealId - id of Deal
   */
  listHistory: async ({ dealId }) => {
    const path = `${API_PATH}/${dealId}/list_history`

    const response = await axios.get(path)

    return response
  },

  /**
   * Get specific history of a deal
   * Invoke `GET admins/sales/deals/get_history
   * @param {Number} historyId - id of Deal
   */
  getHistory: async ({ historyId }) => {
    const path = `${API_PATH}/get_history?history_id=${historyId}`

    const response = await axios.get(path)

    return response
  },

  /**
   * Log Invoice / Customer Receipt print request for audit trail
   * Invoke POST admins/sales/deals/:id/log_print
   */
  logPrint: async ({ dealId, documentType, displayHeaderAndFooter = true }) => {
    const path = `${API_PATH}/${dealId}/log_print`

    const response = await axios.post(path, {
      document_type              : documentType,
      display_header_and_footer : displayHeaderAndFooter
    })

    return response
  },

  /**
   * Send invoice PDF via email
   * Invoke POST admins/sales/deals/:id/send_invoice_email
   */
  sendInvoiceEmail: async ({ dealId, formData }) => {
    const path = `${API_PATH}/${dealId}/send_invoice_email`

    // IMPORTANT: axios will keep baseURL + auth automatically
    const response = await axios.post(path, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })

    return response
  },

  /**
   * Create new payment (termin) for an existing deal
   * Invoke `POST admins/sales/deals/:id/payments`
   *
   * @param {Number} dealId
   * @param {Object} payload
   * @param {String} payload.method_type
   * @param {Number} payload.amount
   * @param {Number} payload.installment
   */
  createPayment: async ({ dealId, payload }) => {
    const path = `${API_PATH}/${dealId}/payments`

    const response = await axios.post(path, payload)

    return response
  },

  /**
   * Upload transfer receipt for a deal payment
   * Invoke `PUT admins/sales/deals/:id/upload_transfer_receipt`
   *
   * @param {Number} dealId
   * @param {Number} paymentId (optional, future-proof)
   * @param {FormData} formData
   */
  uploadTransferReceipt: async ({ dealId, paymentId, formData }) => {
    const path = `${API_PATH}/${dealId}/upload_transfer_receipt`

    // Keep backward compatible, future-proof
    if (paymentId) {
      formData.append('payment_id', String(paymentId))
    }

    const response = await axios.put(path, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

    return response
  },

  /**
   * Get metadata for creating BKO product (designer, category, condition, group)
   * Invoke `GET admins/sales/deals/bko_product_meta`
   */
  poProductMeta: async (rawParams = {}) => {
    const path = `${API_PATH}/po_product_meta`
    const params = ParamsUtil.removeEmptyAttributes(rawParams)

    const response = await axios.get(path, { params })

    return response
  },

  /**
   * Create draft BKO product (free text product for pre-order deal)
   * Invoke `POST admins/sales/deals/create_bko_product`
   *
   * @param {Object} product
   * @param {String} product.supplier_name
   * @param {Number} product.designer_id
   * @param {Number} product.category_id
   * @param {Number} product.condition_id
   * @param {String} product.name
   * @param {String} product.p_size
   * @param {Number} product.default_price
   * @param {Number} product.selling_price
   */
  createBkoProductDraft: async ({ product }) => {
    const path = `${API_PATH}/create_bko_product`

    const params = {
      product,
    }

    const response = await axios.post(path, params)

    return response
  },

  uploadDownPaymentProof: async (dealId, files = []) => {
    const path = `${API_PATH}/${dealId}/upload_down_payment_proof`

    const formData = new FormData()
    files.forEach((file) => {
      formData.append('proofs[]', file)
    })

    const response = await axios.post(path, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

    return response
  },

  /**
   * Cancel one cart item from a Deal
   * Invoke `POST admins/sales/deals/:id/cancel_item
   * @param {Number} dealId - id of Deal
   * @param {Object} payload - { cart_item_id, reason }
   */
  cancelItem: async ({ dealId, payload }) => {
    const path = `${API_PATH}/${dealId}/cancel_item`

    const response = await axios.post(path, payload)

    return response
  },

  /**
   * Cancel specific deals
   * Invoke `GET admins/sales/deals/:id/cancel
   * @param {Number} dealId - id of Deal
   */
  cancel: async ({ dealId }) => {
    const path = `${API_PATH}/${dealId}/cancel`

    const response = await axios.post(path)

    return response
  },


  buyingPoSourcingQueue: async (rawParams = {}) => {
    const defaults = {
      page: 1,
      per_page: 10,
      scope: 'need_action',
      q: '',
      sort_by: 'deals.updated_at',
      sort_dir: 'desc',
    }

    const params = ParamsUtil.removeEmptyAttributes({ ...defaults, ...rawParams })

    return axios.get(BUYING_API_PATH, { params })
  },

  buyingPoSourcingDetail: async (dealId) => {
    const path = `${BUYING_API_PATH}/${dealId}`

    const response = await axios.get(path)

    return response
  },

  buyingStartSourcing: async (dealId, payload = {}) => {
    const path = `${BUYING_API_PATH}/${dealId}/start_sourcing`

    const response = await axios.post(path, payload)

    return response
  },

  buyingSourced: async (dealId, payload = {}) => {
    const path = `${BUYING_API_PATH}/${dealId}/sourced`

    const response = await axios.post(path, payload)

    return response
  },

  buyingCancelSourcedProduct: async (dealId, payload = {}) => {
    const path = `${BUYING_API_PATH}/${dealId}/cancel_sourced_product`

    const response = await axios.post(path, payload)

    return response
  },

  // Existing cancel Deal action tetap dipertahankan.
  buyingCancel: async (dealId) => {
    const path = `${API_PATH}/${dealId}/cancel`

    const response = await axios.post(path)

    return response
  },

  buyingProductMeta: async () => {
    const path = `${BUYING_API_PATH}/product_meta`
    return axios.get(path)
  },

  salesProductMeta: async () => {
    const path = `${API_PATH}/product_meta`
    return axios.get(path)
  },

}

export default SalesDealsApi
